import { useLocation, useNavigate } from 'react-router-dom';
import { Home, ListTodo, UserRound, type LucideIcon } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

interface NavTab {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
}

/** Universal field-app tab bar. Role only changes the content of Tasks,
 *  never the structure. (Analytics is hidden for now.) */
const NAV_TABS: NavTab[] = [
  { id: 'home', label: 'Home', icon: Home, route: '/field' },
  { id: 'tasks', label: 'Tasks', icon: ListTodo, route: '/field/tasks' },
  { id: 'profile', label: 'Profile', icon: UserRound, route: '/field/profile' },
];

/** Work sub-routes that keep the Tasks tab highlighted. */
const TASKS_SUBROUTES = [
  '/field/tasks',
  '/field/activity',
  '/field/capture',
  '/field/crew',
  '/field/visits',
  '/field/visit',
  '/field/checklists',
  '/field/time-tracking',
];

function isTabActive(tab: NavTab, pathname: string): boolean {
  if (tab.id === 'home') return pathname === '/field';
  if (tab.id === 'tasks') return TASKS_SUBROUTES.some((r) => pathname.startsWith(r));
  return pathname === tab.route || pathname.startsWith(`${tab.route}/`);
}

/**
 * Fixed bottom navigation. Four evenly-spaced tabs with a Material-You active
 * pill indicator. Touch targets are ≥44px.
 */
export function FieldBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav
      aria-label="Field app navigation"
      className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-40 tw-flex tw-h-[68px] tw-items-stretch tw-justify-around tw-border-t tw-border-border/60 tw-bg-card/95 tw-shadow-[0_-6px_24px_rgba(15,23,42,0.08)] tw-backdrop-blur-xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
    >
      {NAV_TABS.map((tab) => {
        const isActive = isTabActive(tab, location.pathname);
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => navigate(tab.route)}
            className={cn(
              'tw-flex tw-h-full tw-min-w-[44px] tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-px-1 tw-text-[11px] tw-font-medium tw-transition-colors active:tw-scale-95',
              isActive ? 'tw-text-primary' : 'tw-text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'tw-flex tw-h-8 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-transition-all tw-duration-200',
                isActive ? 'tw-bg-primary/12' : 'tw-bg-transparent',
              )}
            >
              <Icon className="tw-h-[22px] tw-w-[22px]" strokeWidth={isActive ? 2.4 : 1.9} />
            </span>
            <span className={cn('tw-mt-0.5', isActive && 'tw-font-semibold')}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
